/**
 * @file App.tsx
 * @description Main Delivery & Dispatch Console for TableGreet with brand identity.
 */

import React from 'react';
import { DispatchTopBar } from './shared/components/DispatchTopBar';
import { DeliveryOrderCard } from './features/dispatch/components/DeliveryOrderCard';
import { DispatchLoginModal } from './features/auth/components/DispatchLoginModal';
import { useDispatchAuth } from './features/auth/hooks/useDispatchAuth';
import { useDispatchOrders } from './features/dispatch/hooks/useDispatchOrders';
import { Truck, RefreshCw } from 'lucide-react';
import logo from './assets/logo.svg';

export const App: React.FC = () => {
  const { sessionUser, activeVenue, login, logout, isLoading: isAuthLoading } = useDispatchAuth();
  const {
    orders,
    filterStatus,
    setFilterStatus,
    isLoading: isOrdersLoading,
    error,
    changeStatus,
    refreshOrders,
  } = useDispatchOrders(activeVenue?.id || null);

  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center text-zinc-400">
        <img src={logo} className="w-12 h-12 object-contain animate-pulse mb-3" alt="TableGreet" />
        <p className="font-mono text-xs text-zinc-400 tracking-wider">INITIALIZING TABLEGREET DISPATCH TERMINAL...</p>
      </div>
    );
  }

  if (!sessionUser) {
    return <DispatchLoginModal onLogin={login} />;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col overflow-hidden">
      <DispatchTopBar
        venueName={activeVenue?.name || 'TableGreet Dispatch'}
        filterStatus={filterStatus}
        onFilterChange={setFilterStatus}
        orderCount={orders.length}
        onLogout={logout}
      />

      <main className="flex-1 p-4 overflow-y-auto">
        {error && (
          <div className="mb-4 p-3 rounded-xl bg-rose-950/60 border border-rose-800/80 text-rose-300 text-xs font-semibold flex items-center justify-between">
            <span>{error}</span>
            <button
              type="button"
              onClick={refreshOrders}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-rose-900/50 hover:bg-rose-900 text-rose-200 text-xs cursor-pointer"
            >
              <RefreshCw className="w-3 h-3" />
              <span>Retry</span>
            </button>
          </div>
        )}

        {isOrdersLoading && orders.length === 0 ? (
          <div className="h-96 flex flex-col items-center justify-center text-zinc-500">
            <RefreshCw className="w-8 h-8 animate-spin mb-2 text-[#ff4f00]" />
            <p className="text-sm font-medium">Synchronizing delivery queue...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="h-96 flex flex-col items-center justify-center text-zinc-500 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#121212] flex items-center justify-center mb-3 border border-white/10">
              <Truck className="w-8 h-8 text-zinc-600" />
            </div>
            <h3 className="text-base font-bold text-zinc-200">Delivery Queue Clear</h3>
            <p className="text-xs text-zinc-500 mt-1 max-w-xs">
              No active delivery tickets matching the "{filterStatus}" filter right now.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-3.5">
            {orders.map(order => (
              <DeliveryOrderCard
                key={order.id}
                order={order}
                onStatusChange={changeStatus}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default App;
